import { PATIENT_AUTH_STORAGE_KEY } from "../../../constants/patientAuthStorage";
import { treatmentRuntimeApiUrl } from "../runtimeApiBase";
import { clearProgramAuthentication } from "./programAuthenticationStorage";

const REFRESH_PATH = "/auth/token/refresh/";
const SESSION_EXPIRED_MESSAGE = "Your session expired. Please sign in again to continue.";
/** Refresh slightly before expiry so transitions do not race the JWT clock. */
const REFRESH_LEEWAY_MS = 30_000;

let refreshInFlight = null;

export const resetPatientAccessRefreshForTests = () => {
  refreshInFlight = null;
};

export const accessTokenExpiresAtMs = (accessToken) => {
  try {
    const segment = accessToken.split(".")[1];
    const json = atob(segment.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = Number(JSON.parse(json).exp);
    if (Number.isFinite(exp) && exp > 0) return exp * 1000;
  } catch (_error) {
    // Fall through to the server access lifetime.
  }
  return Date.now() + (60 * 60 * 1000);
};

export const storeRefreshedPatientAccessToken = (accessToken) => {
  sessionStorage.setItem(PATIENT_AUTH_STORAGE_KEY.runtimeAccessToken, accessToken);
  sessionStorage.setItem(
    PATIENT_AUTH_STORAGE_KEY.runtimeAccessExpiresAt,
    String(accessTokenExpiresAtMs(accessToken)),
  );
};

export const storedPatientAccessTokenNeedsRefresh = () => {
  const accessToken = sessionStorage.getItem(
    PATIENT_AUTH_STORAGE_KEY.runtimeAccessToken,
  );
  if (!accessToken) return false;
  const storedExpires = Number(
    sessionStorage.getItem(PATIENT_AUTH_STORAGE_KEY.runtimeAccessExpiresAt),
  );
  const jwtExpires = accessTokenExpiresAtMs(accessToken);
  const effectiveExpires = (
    Number.isFinite(storedExpires) && storedExpires > 0
  )
    ? Math.min(storedExpires, jwtExpires)
    : jwtExpires;
  return Date.now() >= effectiveExpires - REFRESH_LEEWAY_MS;
};

/**
 * Exchange the HTTP-only patient refresh cookie for a new access JWT.
 * The refresh token is never read from JavaScript.
 */
export const refreshPatientAccessToken = async (apiBaseUrl) => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const response = await fetch(treatmentRuntimeApiUrl(apiBaseUrl, REFRESH_PATH), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Wellie-Portal": "patient",
      },
      body: "{}",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.access) {
      clearProgramAuthentication();
      const error = new Error(SESSION_EXPIRED_MESSAGE);
      error.code = "patient_session_expired";
      error.status = response.status || 401;
      throw error;
    }
    storeRefreshedPatientAccessToken(data.access);
    return data.access;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
};
